import type { AfterErrorAction } from '@coorum-research/shared-common/api-response/error.schema'
import { headerAccountId, headerSessionKey, headerUserKey } from '@coorum-research/shared-common/types/api-headers'
import axios, { type AxiosInstance, type AxiosRequestConfig } from 'axios'
import { getResearchSettings, getSession } from '../utils/user-session-storage.util'
import { userApi } from './users.api'

export type HttpCatchError = {
  message: string
  status: number
}

class Http {
  instance: AxiosInstance

  constructor() {
    this.instance = axios.create({
      baseURL: import.meta.env.VITE_USER_API_URL as string,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    })
    this.instance.interceptors.request.use(
      async (config) => {
        // sessionが生成される前、つまり、userを取得する前に呼ばれるapiは、accountIdだけ利用するので userIdは sessionから取得でOK
        const { accountId } = getResearchSettings()
        const session = getSession()

        if (accountId) {
          config.headers[headerAccountId] = accountId
        }
        if (session?.sessionKey) {
          config.headers[headerSessionKey] = session.sessionKey
        }
        if (session?.userKey) {
          config.headers[headerUserKey] = session.userKey
        }

        return config
      }, error => {
        console.error(error)
        const message = error?.response?.data?.message || error.message
        const status = error?.response?.status || 500
        return Promise.reject<HttpCatchError>({
          message,
          status,
        })
      })

    this.instance.interceptors.response.use(
      (response) => {
        return response
      }, async (error) => {
        const originalRequest: AxiosRequestConfig & {
          _retry: boolean
        } | undefined = error.config

        console.error(error)
        const afterErrorAction: AfterErrorAction | undefined = error?.response?.data?.afterErrorAction
        if (originalRequest && !originalRequest._retry && afterErrorAction && afterErrorAction === 'session-retry') {
          originalRequest._retry = true
          const { createUserSession } = userApi()
          const { accountId, ...remainSettings } = getResearchSettings()
          await createUserSession(remainSettings)

          return this.instance(originalRequest)
        }

        const message = error?.response?.data?.message || error.message
        const status = error?.response?.status || 500
        return Promise.reject<HttpCatchError>({
          message,
          status,
        })
      },
    )
  }
}

const http = new Http().instance

export default http
